import { lazy, Suspense, type ReactElement } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { ErrorBoundary } from './modules/shared/components/ErrorBoundary';
import { AuthProvider } from './modules/shared/contexts/AuthContext';
import { AppThemeProvider } from './modules/shared/contexts/AppThemeContext';
import { ThemeResolverProvider } from './modules/shared/contexts/ThemeResolverContext';
import { WhiteLabelProvider } from './modules/shared/contexts/WhiteLabelContext';
import { FeatureFlagProvider } from './modules/shared/contexts/FeatureFlagContext';
import { ProgramThemeProvider } from './modules/shared/contexts/ProgramThemeContext';
import { UserThemeProvider } from './modules/shared/contexts/UserThemeContext';
import { ToastProvider } from './modules/shared/components/ui/toast';
import { NotificationServiceProvider } from './modules/shared/components/ui/NotificationService';
import { TooltipProvider } from './modules/shared/components/ui/tooltip';
import { AppPageSkeleton } from './modules/shared/components/ui/AppLoading';
import { DynamicFormProvider } from './modules/programs/contexts/DynamicFormContext';
import { DynamicFormLauncher } from './modules/programs/components/shared/DynamicFormLauncher';
import { OnboardingGate } from './modules/shared/components/OnboardingGate';
import { DemoProvider } from './modules/shared/contexts/DemoContext';
import { DemoControlBar } from './modules/shared/components/DemoControlBar';
import { DemoInstructionPanel, DEMO_PANEL_WIDTH } from './modules/shared/components/DemoInstructionPanel';
import { ExplainProvider, useExplain } from './modules/shared/contexts/ExplainContext';
import { ExplainPanel, EXPLAIN_PANEL_WIDTH } from './modules/shared/components/ExplainPanel';
import { useDemo } from './modules/shared/contexts/DemoContext';
import ProtectedRoute from './modules/shared/components/ProtectedRoute';
import RoleWorkspaceRouteGuard from './modules/shared/components/RoleWorkspaceRouteGuard';
import { useParams } from 'react-router-dom';
import { programRoutes, PROGRAM_ROUTE_SEGMENTS } from './modules/shared/constants/routes';

/** Redirects legacy /hospitals/create-style bookmarks to the unified workflow create route. */
function LegacyWorkflowRedirect({ entityType }: { entityType: 'hospital' | 'hcp' | 'patient' }) {
  const { programId, hospitalId, hcpId } = useParams<{
    programId: string;
    hospitalId?: string;
    hcpId?: string;
  }>();
  if (!programId) return <Navigate to="/dashboard" replace />;
  const query = entityType === 'hcp'
    ? { hospitalId }
    : entityType === 'patient'
      ? { hospitalId, hcpId }
      : undefined;
  return <Navigate to={programRoutes.workflowFormCreate(programId, entityType, query)} replace />;
}

function LegacyRegistrationApprovalRedirect() {
  const { programId, approvalId } = useParams<{ programId: string; approvalId?: string }>();
  if (!programId) return <Navigate to="/dashboard" replace />;

  const target = approvalId
    ? `/programs/${programId}/hcp-dashboard/patient-registration-approvals/${approvalId}`
    : `/programs/${programId}/hcp-dashboard/patient-registration-approvals`;

  return <Navigate to={target} replace />;
}

// ─── EAGER: auth, navigation, layouts ───────────────────────────────────────
import LoginPage from './modules/shared/pages/LoginPage';
import AuthFlowPage from './modules/shared/pages/AuthFlowPage';
import HcpApprovalAutoLoginPage from './modules/shared/pages/HcpApprovalAutoLoginPage';
import DashboardPage from './modules/shared/pages/DashboardPage';
import DynamicLegalPage from './modules/shared/pages/DynamicLegalPage';
import ProgramContextLayout from './modules/programs/components/ProgramContextLayout';
import ProgramLandingRedirect from './modules/programs/components/ProgramLandingRedirect';
import { SuperAdminLayout } from './modules/shared/components/superadmin';
import Layout from './modules/shared/components/Layout';
import ApplicationSettingsLayout from './modules/superadmin/pages/ApplicationSettingsLayout';
import { ProgramModuleGuard } from './modules/programs/guards/ProgramModuleGuard';
import { DashboardAccessGuard } from './modules/programs/guards/DashboardAccessGuard';
import { SUPERADMIN_ROUTE_SEGMENTS } from './modules/superadmin/constants/routes';
import { Patient360ModuleGuard } from './modules/programs/pages/workspace/Patient360ModuleGuard';
import type { Patient360ModuleKey } from './modules/programs/hooks/usePatient360ModuleAvailability';

function withPatient360ModuleGuard(moduleKey: Patient360ModuleKey, element: ReactElement) {
  return <Patient360ModuleGuard moduleKey={moduleKey}>{element}</Patient360ModuleGuard>;
}

// ─── LAZY: SuperAdmin module ─────────────────────────────────────────────────
const SuperAdminDashboardPage     = lazy(() => import('./modules/superadmin/pages/SuperAdminDashboardPage'));
const ErrorLogsPage               = lazy(() => import('./modules/superadmin/pages/ErrorLogsPage'));
const ApplicationSettingsPage     = lazy(() => import('./modules/superadmin/pages/ApplicationSettingsPage'));
const UiSettingsPage              = lazy(() => import('./modules/superadmin/pages/UiSettingsPageV2'));
const FeatureFlagsPage            = lazy(() => import('./modules/superadmin/pages/FeatureFlagsPage'));
const InstructionManagerPage      = lazy(() => import('./modules/superadmin/pages/InstructionManagerPage'));
const LoginPageSettings           = lazy(() => import('./modules/superadmin/pages/LoginPageSettings'));
const GlobalUserListPage          = lazy(() => import('./modules/superadmin/pages/GlobalUserListPage'));
const UserDetailPage              = lazy(() => import('./modules/superadmin/pages/UserDetailPage'));
const UserSummaryPage             = lazy(() => import('./modules/superadmin/pages/UserSummaryPage'));
const UserDetailsEditPage         = lazy(() => import('./modules/superadmin/pages/UserDetailsEditPage'));
const AuditReportPage             = lazy(() => import('./modules/superadmin/pages/AuditReportPage'));
const EmailTemplatesPage          = lazy(() => import('./modules/superadmin/pages/EmailTemplatesPage'));
const LegalPagesManagementPage    = lazy(() => import('./modules/superadmin/pages/LegalPagesManagementPage'));
const RoleTemplateListPage        = lazy(() => import('./modules/superadmin/pages/RoleTemplateListPage'));
const RoleTemplateFormPage        = lazy(() => import('./modules/superadmin/pages/RoleTemplateFormPage'));
const ApplicationPermissionsPage  = lazy(() => import('./modules/superadmin/pages/ApplicationPermissionsPage'));
const HolidayCalendarPage         = lazy(() => import('./modules/superadmin/pages/HolidayCalendarPage'));
const BusinessHoursPage           = lazy(() => import('./modules/superadmin/pages/BusinessHoursPage'));
const NotificationEnginePage      = lazy(() => import('./modules/superadmin/notification-engine/pages/NotificationEnginePage'));
const PermissionMatrixPage        = lazy(() => import('./modules/programs/pages/PermissionMatrixPage').then(m => ({ default: m.PermissionMatrixPage })));
const PermissionAuditLogPage      = lazy(() => import('./modules/programs/pages/PermissionAuditLogPage').then(m => ({ default: m.PermissionAuditLogPage })));
const RoleComparisonPage          = lazy(() => import('./modules/programs/pages/RoleComparisonPage').then(m => ({ default: m.RoleComparisonPage })));
const MasterDataPage              = lazy(() => import('./modules/superadmin/pages/MasterDataPage'));
const ConfigurationPage           = lazy(() => import('./modules/superadmin/pages/ConfigurationPage'));
const DashboardMappingPage        = lazy(() => import('./modules/programs/dashboard-governance/DashboardMappingPage'));

// ─── LAZY: Platform Governance ───────────────────────────────────────────────
const TenantManagementPage        = lazy(() => import('./modules/superadmin/pages/TenantManagementPage'));
const EnvironmentConfigPage       = lazy(() => import('./modules/superadmin/pages/EnvironmentConfigPage'));
const ReleaseManagementPage       = lazy(() => import('./modules/superadmin/pages/ReleaseManagementPage'));
const FeatureFlagPage             = lazy(() => import('./modules/superadmin/pages/FeatureFlagPage'));

// ─── LAZY: Observability ─────────────────────────────────────────────────────
const PlatformOpsDashboardPage    = lazy(() => import('./modules/superadmin/pages/PlatformOpsDashboardPage'));
const SystemHealthPage            = lazy(() => import('./modules/superadmin/pages/SystemHealthPage'));
const SystemAlertsPage            = lazy(() => import('./modules/superadmin/pages/SystemAlertsPage'));
const JobMonitorPage              = lazy(() => import('./modules/superadmin/pages/JobMonitorPage'));
// ─── LAZY: Program Request Portal ────────────────────────────────────────────
const ProgramRequestListPage      = lazy(() => import('./modules/superadmin/pages/ProgramRequestListPage'));
const ProgramRequestDetailPage    = lazy(() => import('./modules/superadmin/pages/ProgramRequestDetailPage'));
const ProgramRequestIntakePage    = lazy(() => import('./modules/superadmin/pages/ProgramRequestIntakePage'));
// ─── LAZY: Program Setup Assistant ───────────────────────────────────────────
const ProgramSetupAssistantPage   = lazy(() => import('./modules/superadmin/pages/ProgramSetupAssistantPage'));
const ProgramSetupPlanReviewPage  = lazy(() => import('./modules/superadmin/pages/ProgramSetupPlanReviewPage'));
const ProgramSetupExecutionPage   = lazy(() => import('./modules/superadmin/pages/ProgramSetupExecutionPage'));
const ProgramSetupHistoryPage     = lazy(() => import('./modules/superadmin/pages/ProgramSetupHistoryPage'));
// ─── LAZY: Program Configuration Studio ──────────────────────────────────────
const ProgramConfigurationImportListPage   = lazy(() => import('./modules/superadmin/pages/ProgramConfigurationImportListPage'));
const ProgramConfigurationImportCreatePage = lazy(() => import('./modules/superadmin/pages/ProgramConfigurationImportCreatePage'));
const ProgramConfigurationImportDetailPage = lazy(() => import('./modules/superadmin/pages/ProgramConfigurationImportDetailPage'));
// ─── LAZY: User Guidance ──────────────────────────────────────────────────────
const UserGuidancePage                     = lazy(() => import('./modules/superadmin/pages/UserGuidancePage'));
// ─── LAZY: Program management ────────────────────────────────────────────────
const ProgramLegalPagesPage             = lazy(() => import('./modules/programs/pages/ProgramLegalPagesPage'));
const ProgramCreatePage                 = lazy(() => import('./modules/programs/pages/ProgramCreatePage'));
const ProgramDashboardPage              = lazy(() => import('./modules/programs/pages/ProgramDashboardPage'));
const ProgramAdminDashboardPage         = lazy(() => import('./modules/programs/pages/ProgramAdminDashboardPage'));
const PharmacovigilancePage             = lazy(() => import('./modules/programs/pages/PharmacovigilancePage'));
const ProgramListPage                   = lazy(() => import('./modules/programs/pages/ProgramListPage'));
const ProgramSelectionPage              = lazy(() => import('./modules/programs/pages/ProgramSelectionPage'));
const ProgramRoleListPage               = lazy(() => import('./modules/programs/pages/ProgramRoleListPage'));
const ProgramRoleFormPage               = lazy(() => import('./modules/programs/pages/ProgramRoleFormPage'));
const RoleCategoryListPage              = lazy(() => import('./modules/programs/pages/RoleCategoryListPage'));
const RoleCategoryFormPage              = lazy(() => import('./modules/programs/pages/RoleCategoryFormPage'));
const ListFieldVisibilityConfigPage     = lazy(() => import('./modules/programs/pages/ListFieldVisibilityConfigPage'));
const ProgramUserAssignmentListPage     = lazy(() => import('./modules/programs/pages/ProgramUserAssignmentListPage'));
const ProgramUserAssignmentFormPage     = lazy(() => import('./modules/programs/pages/ProgramUserAssignmentFormPage'));
const ProgramFormListPage               = lazy(() => import('./modules/programs/pages/ProgramFormListPage'));
const ProgramFormEditPage               = lazy(() => import('./modules/programs/pages/ProgramFormEditPage'));
const FormBuilderPage                   = lazy(() => import('./modules/programs/pages/FormBuilderPage'));
const WorkflowFormMappingPage           = lazy(() => import('./modules/programs/pages/WorkflowFormMappingPage'));
const ProgramWorkflowConfigPage         = lazy(() => import('./modules/programs/pages/ProgramWorkflowConfigPage'));
const WorkflowVisualBuilderPage         = lazy(() => import('./modules/programs/pages/WorkflowVisualBuilderPage'));
const NotificationConfiguredPage        = lazy(() => import('./modules/programs/pages/NotificationConfiguredPage'));
const NotificationCenterPage            = lazy(() => import('./modules/programs/pages/NotificationCenterPage'));
const ProgramCommunicationsPage         = lazy(() => import('./modules/programs/pages/ProgramCommunicationsPage'));
const AutomationRuleListPage            = lazy(() => import('./modules/programs/pages/AutomationRuleListPage'));
const AutomationRuleDetailPage          = lazy(() => import('./modules/programs/pages/AutomationRuleDetailPage'));
const AutomationRuleReviewQueuePage     = lazy(() => import('./modules/programs/pages/AutomationRuleReviewQueuePage'));
const WorkflowDebugPage                 = lazy(() => import('./modules/programs/pages/WorkflowDebugPage'));
const AutoHealingPage                   = lazy(() => import('./modules/programs/pages/AutoHealingPage'));
const AdaptiveWorkflowPage              = lazy(() => import('./modules/programs/pages/AdaptiveWorkflowPage'));
const WorkflowTemplateLibraryPage       = lazy(() => import('./modules/programs/pages/WorkflowTemplateLibraryPage'));
const ProgramAuditReportPage            = lazy(() => import('./modules/programs/pages/ProgramAuditReportPage'));
const ProgramLocationSettingsPage       = lazy(() => import('./modules/programs/pages/ProgramLocationSettings'));
const EntityInheritanceMappingPage      = lazy(() => import('./modules/programs/pages/EntityInheritanceMappingPage'));
const DocumentRequirementsAdminPage     = lazy(() => import('./modules/programs/pages/DocumentRequirementsAdminPage'));
const DocumentBundleAdminPage           = lazy(() => import('./modules/programs/pages/DocumentBundleAdminPage'));
const CompliancePackAdminPage           = lazy(() => import('./modules/programs/pages/CompliancePackAdminPage'));

// ─── LAZY: Entity pages ──────────────────────────────────────────────────────
const HospitalListPage          = lazy(() => import('./modules/programs/pages/HospitalListPage'));
const HospitalViewPage          = lazy(() => import('./modules/programs/pages/HospitalViewPage'));
const HcpViewPage               = lazy(() => import('./modules/programs/pages/HcpViewPage'));
const PatientViewPage           = lazy(() => import('./modules/programs/pages/PatientViewPage'));
const PatientCycleTimelinePage  = lazy(() => import('./modules/programs/pages/PatientCycleTimelinePage'));
const PatientDashboardPage      = lazy(() => import('./modules/programs/components/patient-dashboard/PatientDashboardPage'));

// ─── LAZY: Financial assistance ──────────────────────────────────────────────
const AssistanceSettingsPage  = lazy(() => import('./modules/programs/pages/AssistanceSettingsPage'));
const SelfRegistrationSettingsPage = lazy(() => import('./modules/programs/pages/SelfRegistrationSettingsPage'));
const BenefitPlanListPage     = lazy(() => import('./modules/programs/pages/BenefitPlanListPage'));
const BenefitPlanFormPage     = lazy(() => import('./modules/programs/pages/BenefitPlanFormPage'));
const BenefitPlanViewPage     = lazy(() => import('./modules/programs/pages/BenefitPlanViewPage'));
const Plan4ModelListPage      = lazy(() => import('./modules/programs/pages/Plan4ModelListPage'));
const Plan4ModelFormPage      = lazy(() => import('./modules/programs/pages/Plan4ModelFormPage'));
const Plan4ModelDetailPage    = lazy(() => import('./modules/programs/pages/Plan4ModelDetailPage'));
const AssistanceCaseCreatePage = lazy(() => import('./modules/programs/pages/AssistanceCaseCreatePage'));
const AssistanceCaseListPage  = lazy(() => import('./modules/programs/pages/AssistanceCaseListPage'));
const FixedUtilizationPage    = lazy(() => import('./modules/programs/pages/FixedUtilizationPage'));
const CappingUtilizationPage  = lazy(() => import('./modules/programs/pages/CappingUtilizationPage'));
const FloatingUtilizationPage = lazy(() => import('./modules/programs/pages/FloatingUtilizationPage'));
const HybridUtilizationPage   = lazy(() => import('./modules/programs/pages/HybridUtilizationPage'));
const EligibilityRulesetPage  = lazy(() => import('./modules/programs/pages/EligibilityRulesetPage'));
const AssistanceClaimListPage = lazy(() => import('./modules/programs/pages/AssistanceClaimListPage'));
const AssistanceClaimFormPage = lazy(() => import('./modules/programs/pages/AssistanceClaimFormPage'));

// ─── LAZY: Call logs ──────────────────────────────────────────────────────────
const CallLogListPage               = lazy(() => import('./modules/programs/pages/CallLogListPage'));
const CallLogFormPage               = lazy(() => import('./modules/programs/pages/CallLogFormPage'));
const CallLogSettingsPage           = lazy(() => import('./modules/programs/pages/CallLogSettingsPage'));
const CallLogAnalyticsDashboardPage = lazy(() => import('./modules/programs/pages/CallLogAnalyticsDashboardPage'));
const CallLogFollowUpWorklistPage   = lazy(() => import('./modules/programs/pages/CallLogFollowUpWorklistPage'));

// ─── LAZY: Cycle requests ─────────────────────────────────────────────────────
const CycleRequestsQueuePage = lazy(() => import('./modules/programs/pages/CycleRequestsQueuePage'));

// ─── LAZY: Reports / insights / workspace ────────────────────────────────────
const SlaEscalationListPage       = lazy(() => import('./modules/programs/pages/SlaEscalationListPage'));
const ProgramReportsDashboardPage = lazy(() => import('./modules/programs/pages/ProgramReportsDashboardPage'));
const ProgramDemoGuidePage        = lazy(() => import('./modules/programs/pages/ProgramDemoGuidePage'));
const ActionableInsightsPage      = lazy(() => import('./modules/programs/pages/ActionableInsightsPage'));
const ReportBuilderPage           = lazy(() => import('./modules/programs/pages/ReportBuilderPage'));
const DynamicDashboardPage        = lazy(() => import('./modules/programs/pages/DynamicDashboardPage'));
const ScheduledReportsPage        = lazy(() => import('./modules/programs/pages/ScheduledReportsPage'));
const WorkspacePage               = lazy(() => import('./modules/programs/pages/WorkspacePage'));
const OperationalDashboardPage    = lazy(() => import('./modules/programs/pages/OperationalDashboardPage'));
const FinanceDashboardPage        = lazy(() => import('./modules/programs/pages/FinanceDashboardPage'));
const ScopedWorkspaceDashboardPage = lazy(() => import('./modules/programs/pages/ScopedWorkspaceDashboardPage'));
const HospitalWorkspaceLayout = lazy(() => import('./modules/programs/components/hospital/HospitalWorkspaceLayout'));
const HospitalOverviewPage = lazy(() => import('./modules/programs/pages/hospital/HospitalOverviewPage'));
const HospitalOperationsPage = lazy(() => import('./modules/programs/pages/hospital/HospitalOperationsPage'));
const HospitalPackageRequestsPage = lazy(() => import('./modules/programs/pages/hospital/HospitalPackageRequestsPage'));
const HospitalPatientsPage = lazy(() => import('./modules/programs/pages/hospital/HospitalPatientsPage'));
const HospitalCasesPage = lazy(() => import('./modules/programs/pages/hospital/HospitalCasesPage'));
const HospitalAssistancePage = lazy(() => import('./modules/programs/pages/hospital/HospitalAssistancePage'));
const HospitalAnalyticsPage = lazy(() => import('./modules/programs/pages/hospital/HospitalAnalyticsPage'));
const HospitalCallLogsPage = lazy(() => import('./modules/programs/pages/hospital/HospitalCallLogsPage'));
const HcpWorkspaceLayout = lazy(() => import('./modules/programs/components/hcp/HcpWorkspaceLayout'));
const HcpOverviewPage = lazy(() => import('./modules/programs/pages/hcp/HcpOverviewPage'));
const HcpOperationsPage = lazy(() => import('./modules/programs/pages/hcp/HcpOperationsPage'));
const HcpPatientApprovalPage = lazy(() => import('./modules/programs/pages/hcp/HcpPatientApprovalPage'));
const HcpPatientApprovalReviewPage = lazy(() => import('./modules/programs/pages/hcp/HcpPatientApprovalReviewPage'));
const PatientRegistrationApprovalsPage = lazy(() => import('./modules/programs/pages/hcp/PatientRegistrationApprovalsPage'));
const PatientRegistrationApprovalDetailPage = lazy(() => import('./modules/programs/pages/hcp/PatientRegistrationApprovalDetailPage'));
const HcpNotificationsPage = lazy(() => import('./modules/programs/pages/hcp/HcpNotificationsPage'));
const HcpIntelligencePage = lazy(() => import('./modules/programs/pages/hcp/HcpIntelligencePage'));
const PatientWorkspaceLayout      = lazy(() => import('./modules/programs/pages/PatientWorkspaceLayout'));
const PatientWsOverviewPage       = lazy(() => import('./modules/programs/pages/workspace/OverviewPage'));
const PatientWsCasesPage          = lazy(() => import('./modules/programs/pages/workspace/CasesPage'));
const PatientWsCyclePage          = lazy(() => import('./modules/programs/pages/workspace/CyclePage'));
const PatientWsFinancialsPage     = lazy(() => import('./modules/programs/pages/workspace/FinancialsPage'));
const PatientWsDocumentsPage      = lazy(() => import('./modules/programs/pages/workspace/DocumentsPage'));
const PatientWsCommunicationsPage = lazy(() => import('./modules/programs/pages/workspace/CommunicationsPage'));
const PatientWsCallLogsPage       = lazy(() => import('./modules/programs/pages/workspace/CallLogsPage'));
const PatientWsAEPCPage           = lazy(() => import('./modules/programs/pages/workspace/AEPCPage'));
const PatientWsActivityPage       = lazy(() => import('./modules/programs/pages/workspace/ActivityPage'));

// ─── LAZY: Workspace sub-pages ────────────────────────────────────────────────
// Universal runtime form page — single entry point for all entity create/edit flows.
const RuntimeFormPage                  = lazy(() => import('./modules/programs/pages/runtime/RuntimeFormPage'));
const WorkspaceAssistanceCaseCreatePage = lazy(() => import('./modules/programs/pages/workspace').then(m => ({ default: m.WorkspaceAssistanceCaseCreatePage })));
const WorkspacePatientCasesPage        = lazy(() => import('./modules/programs/pages/workspace').then(m => ({ default: m.WorkspacePatientCasesPage })));
const WorkspaceCaseViewPage            = lazy(() => import('./modules/programs/pages/workspace').then(m => ({ default: m.WorkspaceCaseViewPage })));
const WorkspaceCaseDetailPage          = lazy(() => import('./modules/programs/pages/workspace').then(m => ({ default: m.WorkspaceCaseDetailPage })));
const WorkspaceUtilizationPage         = lazy(() => import('./modules/programs/pages/workspace').then(m => ({ default: m.WorkspaceUtilizationPage })));

import './index.css';

/**
 * DemoLayoutShell – Wraps main content and shifts it left when the demo
 * instruction panel is visible, so the panel never overlaps the app.
 */
function DemoLayoutShell({ children }: { children: React.ReactNode }) {
  const demo = useDemo();
  const explain = useExplain();
  const demoActive = demo.isDemoMode && (demo.isPlaying || demo.isPaused);
  const explainActive = explain.isOpen;
  const padding = (demoActive ? DEMO_PANEL_WIDTH : 0) + (explainActive ? EXPLAIN_PANEL_WIDTH : 0);
  return (
    <div
      className="min-h-screen bg-background transition-[padding] duration-300"
      style={{ paddingRight: padding }}
    >
      {children}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <WhiteLabelProvider>
        <UserThemeProvider>
          <AuthProvider>
            <FeatureFlagProvider>
            <ToastProvider>
            <TooltipProvider delayDuration={200}>
            <NotificationServiceProvider>
            <BrowserRouter>
              <DemoProvider>
              <ExplainProvider>
              <OnboardingGate>
              <DynamicFormProvider>
              <AppThemeProvider>
              <ThemeResolverProvider>
              <ProgramThemeProvider>
              <DemoLayoutShell>
              <DemoControlBar />
              <DemoInstructionPanel />
              <ExplainPanel />
              <Suspense fallback={<AppPageSkeleton />}>
              <Routes>
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/forgot-password" element={<AuthFlowPage />} />
                <Route path="/reset-password" element={<AuthFlowPage />} />
                <Route path="/hcp-approval" element={<HcpApprovalAutoLoginPage />} />
                
                {/* Public Legal Pages */}
                <Route path="/legal/:pageKey" element={<DynamicLegalPage />} />
                
                {/* Public Program Request Intake */}
                <Route path="/program-request/intake/:token" element={<ProgramRequestIntakePage />} />

                {/* Generic Dashboard - redirects to appropriate location */}
                <Route element={<Layout />}>
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <ErrorBoundary>
                      <DashboardPage />
                    </ErrorBoundary>
                  </ProtectedRoute>
                } />
                
                {/* Program-specific login route */}
                <Route path="/p/:programCode/login" element={<LoginPage />} />
                <Route path="/p/:programCode/forgot-password" element={<AuthFlowPage />} />
                <Route path="/p/:programCode/reset-password" element={<AuthFlowPage />} />
                
                {/* Protected SuperAdmin Routes */}
                <Route path="/superadmin" element={
                  <ProtectedRoute requiredRole={["SuperAdmin", "ProductAdmin"]}>
                    <SuperAdminLayout />
                  </ProtectedRoute>
                }>
                  <Route index element={<Navigate to={SUPERADMIN_ROUTE_SEGMENTS.DASHBOARD} replace />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.DASHBOARD} element={<SuperAdminDashboardPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.ERROR_LOGS} element={<ErrorLogsPage />} />
                  <Route path="feature-flags" element={<FeatureFlagsPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.APPLICATION_SETTINGS} element={<ApplicationSettingsLayout />}>
                    <Route index element={<ApplicationSettingsPage />} />
                    <Route path={SUPERADMIN_ROUTE_SEGMENTS.LOGIN_PAGE_SETTINGS} element={<LoginPageSettings />} />
                    <Route path={SUPERADMIN_ROUTE_SEGMENTS.UI_SETTINGS} element={<UiSettingsPage />} />
                    <Route path={SUPERADMIN_ROUTE_SEGMENTS.INSTRUCTION_MANAGER} element={<InstructionManagerPage />} />
                    <Route path={SUPERADMIN_ROUTE_SEGMENTS.EMAIL_TEMPLATES} element={<EmailTemplatesPage />} />
                    <Route path={SUPERADMIN_ROUTE_SEGMENTS.LEGAL_PAGES} element={<LegalPagesManagementPage />} />
                    <Route path={SUPERADMIN_ROUTE_SEGMENTS.NOTIFICATION_ENGINE} element={<NotificationEnginePage />} />
                  </Route>
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.USERS} element={<GlobalUserListPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.USERS}/:userId/${SUPERADMIN_ROUTE_SEGMENTS.USER_SUMMARY}`} element={<UserSummaryPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.USERS}/:userId/${SUPERADMIN_ROUTE_SEGMENTS.USER_EDIT}`} element={<UserDetailsEditPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.USERS}/:userId`} element={<UserDetailPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.AUDIT_REPORT} element={<AuditReportPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.PERMISSION_MATRIX} element={<PermissionMatrixPage scope="APPLICATION" />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.PERMISSION_AUDIT} element={<PermissionAuditLogPage scope="APPLICATION" />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.ROLE_COMPARISON} element={<RoleComparisonPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.ROLE_TEMPLATES} element={<RoleTemplateListPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.ROLE_TEMPLATES}/${SUPERADMIN_ROUTE_SEGMENTS.ROLE_TEMPLATES_CREATE}`} element={<RoleTemplateFormPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.ROLE_TEMPLATES}/:templateId`} element={<RoleTemplateFormPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.ROLE_TEMPLATES}/:templateId/${SUPERADMIN_ROUTE_SEGMENTS.USER_EDIT}`} element={<RoleTemplateFormPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.APPLICATION_PERMISSIONS} element={<ApplicationPermissionsPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.HOLIDAY_CALENDARS} element={<HolidayCalendarPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.BUSINESS_HOURS} element={<BusinessHoursPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.MASTER_DATA} element={<MasterDataPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.CONFIGURATION} element={<ConfigurationPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.DASHBOARD_GOVERNANCE} element={<DashboardMappingPage />} />
                  {/* Platform Governance */}
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.TENANTS} element={<TenantManagementPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.ENVIRONMENTS} element={<EnvironmentConfigPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.RELEASES} element={<ReleaseManagementPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.FEATURE_FLAGS} element={<FeatureFlagPage />} />
                  {/* Observability */}
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.PLATFORM_OPS} element={<PlatformOpsDashboardPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.SYSTEM_HEALTH} element={<SystemHealthPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.SYSTEM_ALERTS} element={<SystemAlertsPage />} />
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.JOB_MONITOR} element={<JobMonitorPage />} />
                  {/* Program Governance */}
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_GOVERNANCE} element={<Navigate to="/programs" replace />} />
                  {/* Program Request Portal */}
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_REQUESTS} element={<ProgramRequestListPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_REQUESTS}/:requestId`} element={<ProgramRequestDetailPage />} />
                  {/* Program Setup Assistant */}
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_SETUP}/new`} element={<ProgramSetupAssistantPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_SETUP}/plans/:planId`} element={<ProgramSetupPlanReviewPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_SETUP}/plans/:planId/execution`} element={<ProgramSetupExecutionPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_SETUP}/history`} element={<ProgramSetupHistoryPage />} />
                  {/* Program Configuration Studio */}
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_CONFIGURATION_STUDIO} element={<ProgramConfigurationImportListPage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_CONFIGURATION_STUDIO}/new`} element={<ProgramConfigurationImportCreatePage />} />
                  <Route path={`${SUPERADMIN_ROUTE_SEGMENTS.PROGRAM_CONFIGURATION_STUDIO}/:importId`} element={<ProgramConfigurationImportDetailPage />} />
                  {/* User Guidance */}
                  <Route path={SUPERADMIN_ROUTE_SEGMENTS.USER_GUIDANCE} element={<UserGuidancePage />} />
                </Route>
              
              {/* Protected Program Routes */}
              <Route path="/programs" element={
                <ProtectedRoute requiredRole={["SuperAdmin", "ProductAdmin"]}>
                  <ProgramListPage />
                </ProtectedRoute>
              } />
              <Route path="/programs/create" element={
                <ProtectedRoute requiredRole={["SuperAdmin", "ProductAdmin"]}>
                  <ProgramCreatePage />
                </ProtectedRoute>
              } />
              <Route path="/programs/select" element={
                <ProtectedRoute>
                  <ProgramSelectionPage />
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId" element={
                <ProtectedRoute>
                  <ProgramContextLayout />
                </ProtectedRoute>
              }>
                {/* Index route: redirect to role-appropriate landing page */}
                <Route index element={<ProgramLandingRedirect />} />
                <Route path="dashboard" element={
                  <ProgramModuleGuard menuKey="PROGRAM_DASHBOARD">
                    <RoleWorkspaceRouteGuard routeKind="dashboard">
                      <ErrorBoundary>
                        <ProgramDashboardPage />
                      </ErrorBoundary>
                    </RoleWorkspaceRouteGuard>
                  </ProgramModuleGuard>
                } />
                <Route path="pharmacovigilance" element={
                  <ErrorBoundary>
                    <PharmacovigilancePage />
                  </ErrorBoundary>
                } />
                <Route path="legal/:pageKey" element={<DynamicLegalPage />} />
                <Route path="roles" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <ProgramRoleListPage />
                  </ProgramModuleGuard>
                } />
                <Route path="roles/create" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <ProgramRoleFormPage />
                  </ProgramModuleGuard>
                } />
                <Route path="roles/:roleId/edit" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <ProgramRoleFormPage />
                  </ProgramModuleGuard>
                } />
                <Route path="role-categories" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <RoleCategoryListPage />
                  </ProgramModuleGuard>
                } />
                <Route path="role-categories/create" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <RoleCategoryFormPage />
                  </ProgramModuleGuard>
                } />
                <Route path="role-categories/:categoryId/edit" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <RoleCategoryFormPage />
                  </ProgramModuleGuard>
                } />
                <Route path="list-field-visibility" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <ListFieldVisibilityConfigPage />
                  </ProgramModuleGuard>
                } />
                <Route path="permissions-matrix" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <PermissionMatrixPage scope="PROGRAM" />
                  </ProgramModuleGuard>
                } />
                <Route path="permissions" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <PermissionMatrixPage scope="PROGRAM" />
                  </ProgramModuleGuard>
                } />
                <Route path="audit/permissions" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <PermissionAuditLogPage scope="PROGRAM" />
                  </ProgramModuleGuard>
                } />
                <Route path="user-assignments" element={
                  <ProgramModuleGuard menuKey="USERS_CONFIG">
                    <ProgramUserAssignmentListPage />
                  </ProgramModuleGuard>
                } />
                <Route path="user-assignments/create" element={
                  <ProgramModuleGuard menuKey="USERS_CONFIG">
                    <ProgramUserAssignmentFormPage />
                  </ProgramModuleGuard>
                } />
                <Route path="user-assignments/:assignmentId/edit" element={
                  <ProgramModuleGuard menuKey="USERS_CONFIG">
                    <ProgramUserAssignmentFormPage />
                  </ProgramModuleGuard>
                } />
                <Route path="forms" element={
                  <ProgramModuleGuard menuKey="FORMS_CONFIG">
                    <ProgramFormListPage />
                  </ProgramModuleGuard>
                } />
                <Route path="forms/builder" element={
                  <ProgramModuleGuard menuKey="FORMS_CONFIG">
                    <FormBuilderPage />
                  </ProgramModuleGuard>
                } />
                <Route path="forms/:formId/edit" element={
                  <ProgramModuleGuard menuKey="FORMS_CONFIG">
                    <ProgramFormEditPage />
                  </ProgramModuleGuard>
                } />
                <Route path="forms/:formId/builder" element={
                  <ProgramModuleGuard menuKey="FORMS_CONFIG">
                    <FormBuilderPage />
                  </ProgramModuleGuard>
                } />
                <Route path="workflow" element={
                  <ProgramModuleGuard menuKey="WORKFLOW_CONFIG">
                    <ProgramWorkflowConfigPage />
                  </ProgramModuleGuard>
                } />
                <Route path="workflow/visual" element={
                  <ProgramModuleGuard menuKey="WORKFLOW_CONFIG">
                    <WorkflowVisualBuilderPage />
                  </ProgramModuleGuard>
                } />
                <Route path="workflows/:workflowId/visual" element={
                  <ProgramModuleGuard menuKey="WORKFLOW_CONFIG">
                    <WorkflowVisualBuilderPage />
                  </ProgramModuleGuard>
                } />
                <Route path="workflow-debug" element={
                  <ProgramModuleGuard menuKey="WORKFLOW_CONFIG">
                    <WorkflowDebugPage />
                  </ProgramModuleGuard>
                } />
                <Route path="auto-healing" element={
                  <ProgramModuleGuard menuKey="WORKFLOW_CONFIG">
                    <AutoHealingPage />
                  </ProgramModuleGuard>
                } />
                <Route path="adaptive-workflows" element={
                  <ProgramModuleGuard menuKey="WORKFLOW_CONFIG">
                    <AdaptiveWorkflowPage />
                  </ProgramModuleGuard>
                } />
                <Route path="workflow-templates" element={
                  <ProgramModuleGuard menuKey="WORKFLOW_CONFIG">
                    <WorkflowTemplateLibraryPage />
                  </ProgramModuleGuard>
                } />
                <Route path="workflows/:workflowId/form-mapping" element={
                  <ProgramModuleGuard menuKey="WORKFLOW_CONFIG">
                    <WorkflowFormMappingPage />
                  </ProgramModuleGuard>
                } />
                <Route path="notification-configured" element={
                  <ProgramModuleGuard menuKey="NOTIFICATIONS_CONFIG">
                    <NotificationConfiguredPage />
                  </ProgramModuleGuard>
                } />
                <Route path="notifications" element={
                  <ProgramModuleGuard menuKey="NOTIFICATIONS_CONFIG">
                    <NotificationConfiguredPage />
                  </ProgramModuleGuard>
                } />
                <Route path="notifications/inbox" element={<NotificationCenterPage />} />
                <Route path="communications" element={<ProgramCommunicationsPage />} />
                <Route path="automation-rules" element={
                  <ProgramModuleGuard menuKey="AUTOMATION_RULES">
                    <AutomationRuleListPage />
                  </ProgramModuleGuard>
                } />
                <Route path="automation-rules/review-queue" element={
                  <ProgramModuleGuard menuKey="AUTOMATION_RULE_REVIEW">
                    <AutomationRuleReviewQueuePage />
                  </ProgramModuleGuard>
                } />
                <Route path="automation-rules/:ruleId" element={
                  <ProgramModuleGuard menuKey="AUTOMATION_RULES">
                    <AutomationRuleDetailPage />
                  </ProgramModuleGuard>
                } />
                <Route path="audit-report" element={
                  <ProgramModuleGuard menuKey="AUDIT_REPORT">
                    <ProgramAuditReportPage />
                  </ProgramModuleGuard>
                } />
                <Route path="ui-settings" element={
                  <ProgramModuleGuard menuKey="UI_SETTINGS">
                    <UiSettingsPage />
                  </ProgramModuleGuard>
                } />
                <Route path="legal-pages" element={<ProgramLegalPagesPage />} />
                <Route path={PROGRAM_ROUTE_SEGMENTS.LOCATION_SETTINGS} element={<ProgramLocationSettingsPage />} />
                <Route path={PROGRAM_ROUTE_SEGMENTS.ENTITY_INHERITANCE} element={<EntityInheritanceMappingPage />} />
                <Route path={PROGRAM_ROUTE_SEGMENTS.DOCUMENT_REQUIREMENTS} element={<DocumentRequirementsAdminPage />} />
                <Route path={PROGRAM_ROUTE_SEGMENTS.DOCUMENT_BUNDLES} element={<DocumentBundleAdminPage />} />
                <Route path={PROGRAM_ROUTE_SEGMENTS.COMPLIANCE_PACKS} element={<CompliancePackAdminPage />} />

                {/* Unified Runtime Form — single entry point for ALL entity create/edit flows.
                    Route shape: /programs/:programId/runtime/:context/:mode[/:entityId]
                      • context : lowercase RuntimeContextKey (hospital, hcp, patient, scp, callLog, pharmacy, dispenseCase)
                      • mode    : 'create' | 'edit'
                    Nested under ProgramLayout so sidebar/header/breadcrumbs remain visible. */}
                <Route path="runtime/:context/:mode" element={
                  <ProgramModuleGuard menuKey="WORKSPACE" allowSuperAdmin={false}>
                    <RuntimeFormPage />
                  </ProgramModuleGuard>
                } />
                <Route path="runtime/:context/:mode/:entityId" element={
                  <ProgramModuleGuard menuKey="WORKSPACE" allowSuperAdmin={false}>
                    <RuntimeFormPage />
                  </ProgramModuleGuard>
                } />
              </Route>
              {/* Hospital Management Routes */}
              <Route path="/programs/:programId/hospitals" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="HOSPITALS">
                    <HospitalListPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              
              {/* Legacy /hospitals/create bookmarks → redirect to workflow route */}
              <Route path="/programs/:programId/hospitals/create" element={
                <ProtectedRoute>
                  <LegacyWorkflowRedirect entityType="hospital" />
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/hospitals/:hospitalId/view" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="HOSPITALS">
                    <HospitalViewPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              
              {/* HCP Routes under Hospital */}
              <Route path="/programs/:programId/hospitals/:hospitalId/hcps/:hcpId/view" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="HCPS">
                    <HcpViewPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              
              {/* Patient Routes under HCP */}
              <Route path="/programs/:programId/hospitals/:hospitalId/hcps/:hcpId/patients/:patientId/view" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="PATIENTS">
                    <PatientViewPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              
              {/* Patient Dashboard Route */}
              <Route path="/programs/:programId/patients/:patientId/dashboard" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="PATIENTS">
                    <PatientDashboardPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              <Route path="/programs/:programId/patients/:patientId/cycles" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="PATIENTS">
                    <PatientCycleTimelinePage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              
              {/* Financial Assistance Routes */}
              <Route path="/programs/:programId/assistance-settings" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="ASSISTANCE_SETTINGS">
                    <AssistanceSettingsPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/self-registration-settings" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="ASSISTANCE_SETTINGS">
                    <SelfRegistrationSettingsPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/benefit-plans" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="FINANCIAL_PLAN">
                    <BenefitPlanListPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/benefit-plans/create" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="FINANCIAL_PLAN">
                    <BenefitPlanFormPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/benefit-plans/:benefitPlanId" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="FINANCIAL_PLAN">
                    <BenefitPlanViewPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/benefit-plans/:benefitPlanId/edit" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="FINANCIAL_PLAN">
                    <BenefitPlanFormPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              {/* Financial Assistance 4-Models Routes */}
              <Route path="/programs/:programId/plans4model" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="FINANCIAL_PLAN">
                    <Plan4ModelListPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/plans4model/create/:modelType" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="FINANCIAL_PLAN">
                    <Plan4ModelFormPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/plans4model/:planId" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="FINANCIAL_PLAN">
                    <Plan4ModelDetailPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/plans4model/:planId/edit" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="FINANCIAL_PLAN">
                    <Plan4ModelFormPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/financial-assistance/cases/create" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CREATE_ASSISTANCE_CASE" allowSuperAdmin={false}>
                    <AssistanceCaseCreatePage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/financial-assistance/cases" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="ASSISTANCE_CASES" allowSuperAdmin={false}>
                    <AssistanceCaseListPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/cases/:caseId/utilize/fixed" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CREATE_ASSISTANCE_CASE" allowSuperAdmin={false}>
                    <FixedUtilizationPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/cases/:caseId/utilize/capping" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CREATE_ASSISTANCE_CASE" allowSuperAdmin={false}>
                    <CappingUtilizationPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/cases/:caseId/utilize/floating" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CREATE_ASSISTANCE_CASE" allowSuperAdmin={false}>
                    <FloatingUtilizationPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/cases/:caseId/utilize/hybrid" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CREATE_ASSISTANCE_CASE" allowSuperAdmin={false}>
                    <HybridUtilizationPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              {/* Legacy route redirected to workspace - keeping for backward compatibility redirect */}
              {/* <Route path="/programs/:programId/cases/:caseId/financial-summary" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CREATE_ASSISTANCE_CASE">
                    <CaseSummary4ModelPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } /> */}
              
              {/* Eligibility Rulesets */}
              <Route path="/programs/:programId/eligibility-rulesets" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CREATE_ASSISTANCE_CASE">
                    <EligibilityRulesetPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              {/* Assistance Claims */}
              <Route path="/programs/:programId/assistance-claims" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="ASSISTANCE_CLAIMS">
                    <AssistanceClaimListPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/assistance-claims/new" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="ASSISTANCE_CLAIMS">
                    <AssistanceClaimFormPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/assistance-claims/:claimId" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="ASSISTANCE_CLAIMS">
                    <AssistanceClaimFormPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              {/* Call Logs */}
              <Route path="/programs/:programId/call-logs" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CALL_LOGS">
                    <CallLogListPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/call-logs/new" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CALL_LOGS">
                    <CallLogFormPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/call-logs/:callLogId" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CALL_LOGS">
                    <CallLogFormPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/call-log-settings" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CALL_LOGS">
                    <CallLogSettingsPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/call-logs/analytics" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CALL_LOGS">
                    <CallLogAnalyticsDashboardPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              <Route path="/programs/:programId/call-logs/follow-ups" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CALL_LOGS">
                    <CallLogFollowUpWorklistPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              {/* SLA Escalations */}
              <Route path="/programs/:programId/sla-escalations" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="CREATE_ASSISTANCE_CASE">
                    <SlaEscalationListPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              {/* Reporting & Analytics */}
              <Route path="/programs/:programId/reports" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="REPORTS">
                    <ProgramReportsDashboardPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              <Route path="/programs/:programId/demo-guide" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="REPORTS">
                    <ProgramDemoGuidePage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              {/* Actionable Insights */}
              <Route path="/programs/:programId/insights" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="REPORTS">
                    <ActionableInsightsPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              {/* Report Builder */}
              <Route path="/programs/:programId/report-builder" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="REPORTS">
                    <ReportBuilderPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              {/* Dynamic Dashboard (charts + KPIs from report engine) */}
              <Route path="/programs/:programId/dynamic-dashboard" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="REPORTS">
                    <DynamicDashboardPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />

              {/* Scheduled Reports */}
              <Route path="/programs/:programId/scheduled-reports" element={
                <ProtectedRoute>
                  <ProgramModuleGuard menuKey="REPORTS">
                    <ScheduledReportsPage />
                  </ProgramModuleGuard>
                </ProtectedRoute>
              } />
              {/* Program workspace routes — now inside Layout wrapper for sidebar */}
              <Route path="/programs/:programId" element={
                <ProtectedRoute>
                  <ProgramContextLayout />
                </ProtectedRoute>
              }>
                {/* Operational Dashboard for CC/CCM/Program Admin */}
                <Route path="operational-dashboard" element={
                  <ProgramModuleGuard menuKey="OPERATIONAL_DASHBOARD">
                    <OperationalDashboardPage />
                  </ProgramModuleGuard>
                } />
                {/* Finance Dashboard — Finance Team landing route */}
                <Route path="finance-dashboard" element={
                  <ProgramModuleGuard menuKey="FINANCE_DASHBOARD" allowSuperAdmin={false}>
                    <DashboardAccessGuard>
                      <FinanceDashboardPage />
                    </DashboardAccessGuard>
                  </ProgramModuleGuard>
                } />
                {/* Deep-link routes for entity detail view (renders WorkspacePage, which auto-selects entity) */}
                <Route path="workspace/hospitals/:hospitalId" element={
                  <ProgramModuleGuard menuKey="WORKSPACE" allowSuperAdmin={false}>
                    <RoleWorkspaceRouteGuard routeKind="program">
                      <WorkspacePage />
                    </RoleWorkspaceRouteGuard>
                  </ProgramModuleGuard>
                } />
                <Route path="workspace/hcps/:hcpId" element={
                  <ProgramModuleGuard menuKey="WORKSPACE" allowSuperAdmin={false}>
                    <RoleWorkspaceRouteGuard routeKind="program">
                      <WorkspacePage />
                    </RoleWorkspaceRouteGuard>
                  </ProgramModuleGuard>
                } />
                {/* Cycle request queue (CC/CCM). Own route: the "workspace" parent below renders WorkspacePage
                    without an <Outlet/>, so a nested child never displayed. Access to actions is enforced by
                    CYCLE_REQUEST_* permissions on the page and the API. */}
                <Route path="workspace/cycle-requests" element={
                  <ProgramModuleGuard menuKey="WORKSPACE" allowSuperAdmin={false}>
                    <RoleWorkspaceRouteGuard routeKind="program">
                      <CycleRequestsQueuePage />
                    </RoleWorkspaceRouteGuard>
                  </ProgramModuleGuard>
                } />
                <Route path="workspace/patients/:patientId" element={
                  <ProgramModuleGuard menuKey="WORKSPACE" allowSuperAdmin={false}>
                    <RoleWorkspaceRouteGuard routeKind="program">
                      <WorkspacePage />
                    </RoleWorkspaceRouteGuard>
                  </ProgramModuleGuard>
                } />
                <Route path="workspace" element={
                  <ProgramModuleGuard menuKey="WORKSPACE" allowSuperAdmin={false}>
                    <RoleWorkspaceRouteGuard routeKind="program">
                      <WorkspacePage />
                    </RoleWorkspaceRouteGuard>
                  </ProgramModuleGuard>
                }>
                  {/* Nested workspace assistance case create route (no formId - uses API form resolution) */}
                  <Route path="patients/:hospitalId/:hcpId/:patientId/assistance/create" element={<WorkspaceAssistanceCaseCreatePage />} />
                  {/* Nested workspace patient cases list and detail routes */}
                  <Route path="patients/:hospitalId/:hcpId/:patientId/cases" element={<WorkspacePatientCasesPage />} />
                  <Route path="patients/:hospitalId/:hcpId/:patientId/cases/:caseId" element={<WorkspaceCaseViewPage />} />
                  <Route path="patients/:hospitalId/:hcpId/:patientId/cases/:caseId/utilize/:type" element={<WorkspaceUtilizationPage />} />
                  {/* All program-level assistance cases */}
                  <Route path="cases" element={<AssistanceCaseListPage />} />
                  {/* Direct case detail - /workspace/cases/:caseId */}
                  <Route path="cases/:caseId" element={<WorkspaceCaseDetailPage />} />
                  {/* Legacy nested workspace create/edit routes have been removed.
                      All entity create/edit flows now route through the unified
                      /programs/:programId/runtime/:context/:mode[/:entityId] route. */}
                </Route>
                {/* Unified Workspace Route - Single entry point for all non-admin users */}
                {/* UNIFIED WORKSPACE: All role-specific workspace routes removed.
                    All users now access single /workspace route with role-based rendering. */}
                {/* HCPs & Patients - Route to Workspace for unified entity management */}
                <Route path="hcps" element={
                  <ProgramModuleGuard menuKey="HCPS">
                    <WorkspacePage />
                  </ProgramModuleGuard>
                } />
                <Route path="patients" element={
                  <ProgramModuleGuard menuKey="PATIENTS">
                    <WorkspacePage />
                  </ProgramModuleGuard>
                } />
                {/* Patient 360 Workspace — nested child routes with persistent header */}
                <Route path="patients/:patientId/360" element={
                  <ProgramModuleGuard menuKey="PATIENTS">
                    <ErrorBoundary>
                      <PatientWorkspaceLayout />
                    </ErrorBoundary>
                  </ProgramModuleGuard>
                }>
                  <Route index element={<PatientWsOverviewPage />} />
                  <Route path="cases" element={<ErrorBoundary severity="section"><PatientWsCasesPage /></ErrorBoundary>} />
                  <Route path="cycle" element={<ErrorBoundary severity="section"><PatientWsCyclePage /></ErrorBoundary>} />
                  <Route path="financials" element={<PatientWsFinancialsPage />} />
                  <Route path="documents" element={<ErrorBoundary severity="section"><PatientWsDocumentsPage /></ErrorBoundary>} />
                  <Route path="communications" element={<PatientWsCommunicationsPage />} />
                  <Route path="call-logs" element={withPatient360ModuleGuard('call-logs', <PatientWsCallLogsPage />)} />
                  <Route path="ae-pc" element={withPatient360ModuleGuard('ae-pc', <PatientWsAEPCPage />)} />
                  <Route path="activity" element={
                    <ProgramModuleGuard menuKey="PATIENT_ACTIVITY">
                      <PatientWsActivityPage />
                    </ProgramModuleGuard>
                  } />
                </Route>

                {/* Scoped Workspace Dashboard – read-only KPI/chart dashboard
                    reusable for Hospital and HCP roles. Scope is resolved
                    server-side from JWT claims (HospitalId / HcpId); the
                    backend returns 403 for any caller without a scope claim. */}
                <Route path="scoped-dashboard" element={
                  <ProgramModuleGuard menuKey="WORKSPACE" allowSuperAdmin={false}>
                    <DashboardAccessGuard>
                      <RoleWorkspaceRouteGuard routeKind="program">
                        <ScopedWorkspaceDashboardPage />
                      </RoleWorkspaceRouteGuard>
                    </DashboardAccessGuard>
                  </ProgramModuleGuard>
                } />

                {/* Program Admin Dashboard — dedicated governance/configuration overview */}
                <Route path="admin-dashboard" element={
                  <ProgramModuleGuard menuKey="ROLES_CONFIG">
                    <ErrorBoundary>
                      <ProgramAdminDashboardPage />
                    </ErrorBoundary>
                  </ProgramModuleGuard>
                } />

                {/* Role-specific landing dashboards. All three reuse
                    ScopedWorkspaceDashboardPage; backend resolves scope from
                    JWT (HospitalId / HcpId / future PatientId). The route
                    segment is the configuration-driven landing target
                    returned by `MenuKeys.GetDefaultLandingRoute`. */}
                <Route path="hospital-dashboard" element={
                  <ProgramModuleGuard menuKey="HOSPITAL_WORKSPACE" allowSuperAdmin={false}>
                    <DashboardAccessGuard>
                      <RoleWorkspaceRouteGuard routeKind="program">
                        <HospitalWorkspaceLayout />
                      </RoleWorkspaceRouteGuard>
                    </DashboardAccessGuard>
                  </ProgramModuleGuard>
                }>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<HospitalOverviewPage />} />
                  <Route path="operations" element={<HospitalOperationsPage />} />
                  <Route path="patients" element={<HospitalPatientsPage />} />
                  <Route path="cases" element={<HospitalCasesPage />} />
                  <Route path="assistance" element={<HospitalAssistancePage />} />
                  <Route path="analytics" element={<HospitalAnalyticsPage />} />
                  {/* intelligence consolidated into overview — redirect for backwards compatibility */}
                  <Route path="intelligence" element={<Navigate to="../overview" replace />} />
                  <Route path="package-requests" element={<HospitalPackageRequestsPage />} />
                  <Route path="call-logs" element={<HospitalCallLogsPage />} />
                </Route>
                <Route path="hcp-dashboard" element={
                  <ProgramModuleGuard menuKey="HCP_WORKSPACE" allowSuperAdmin={false}>
                    <DashboardAccessGuard>
                      <RoleWorkspaceRouteGuard routeKind="program">
                        <HcpWorkspaceLayout />
                      </RoleWorkspaceRouteGuard>
                    </DashboardAccessGuard>
                  </ProgramModuleGuard>
                }>
                  <Route index element={<Navigate to="overview" replace />} />
                  <Route path="overview" element={<HcpOverviewPage />} />
                  <Route path="operations" element={<HcpOperationsPage />} />
                  <Route path="patient-approvals" element={<HcpPatientApprovalPage />} />
                  <Route path="patient-approval-review" element={<HcpPatientApprovalReviewPage />} />
                  <Route path="patient-registration-approvals" element={<PatientRegistrationApprovalsPage />} />
                  <Route path="patient-registration-approvals/:approvalId" element={<PatientRegistrationApprovalDetailPage />} />
                  <Route path="notifications" element={<HcpNotificationsPage />} />
                  <Route path="intelligence" element={<HcpIntelligencePage />} />
                </Route>
                <Route path="patient-registration-approvals" element={<LegacyRegistrationApprovalRedirect />} />
                <Route path="patient-registration-approvals/:approvalId" element={<LegacyRegistrationApprovalRedirect />} />
                <Route path="patient-dashboard" element={
                  <ProgramModuleGuard menuKey="PATIENT_WORKSPACE" allowSuperAdmin={false}>
                    <DashboardAccessGuard>
                      <RoleWorkspaceRouteGuard routeKind="program">
                        <PatientDashboardPage />
                      </RoleWorkspaceRouteGuard>
                    </DashboardAccessGuard>
                  </ProgramModuleGuard>
                } />
                <Route path="pharma-dashboard" element={
                  <ProgramModuleGuard menuKey="PHARMA_WORKSPACE" allowSuperAdmin={false}>
                    <DashboardAccessGuard>
                      <RoleWorkspaceRouteGuard routeKind="program">
                        <ScopedWorkspaceDashboardPage />
                      </RoleWorkspaceRouteGuard>
                    </DashboardAccessGuard>
                  </ProgramModuleGuard>
                } />
              </Route>
              </Route>{/* End Layout wrapper */}

              {/* Unauthorized Page */}
              <Route path="/unauthorized" element={
                <div className="min-h-screen flex items-center justify-center bg-muted">
                  <div className="text-center">
                    <h1 className="text-4xl font-bold text-foreground mb-4">403</h1>
                    <p className="text-muted-foreground mb-6">You don't have permission to access this page.</p>
                    <a href="/login" className="text-primary hover:text-primary-hover font-medium">
                      Return to Login
                    </a>
                  </div>
                </div>
              } />
            </Routes>
              </Suspense>
              </DemoLayoutShell>
              </ProgramThemeProvider>
              </ThemeResolverProvider>
              </AppThemeProvider>
              <DynamicFormLauncher />
              </DynamicFormProvider>
              </OnboardingGate>
              </ExplainProvider>
              </DemoProvider>
            </BrowserRouter>
      </NotificationServiceProvider>
      </TooltipProvider>
      </ToastProvider>
      </FeatureFlagProvider>
      </AuthProvider>
      </UserThemeProvider>
      </WhiteLabelProvider>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
